'use client'

import { cn } from '@/core/lib/utils/cn'
import { Motion } from '@/core/ui/atoms/motion'
import { useInView } from 'framer-motion'
import { CarTaxiFront, MapPin, Users } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

type Stat = {
  label: string
  value: number
  icon: React.ReactNode
  color: string
}

type Props = {
  className?: string
}

export const Stats: React.FC<Props> = ({ className }) => {
  const t = useTranslations('stats')

  const stats: Stat[] = [
    {
      label: t('trips'),
      value: 1000,
      icon: <CarTaxiFront className='h-10 w-10' />,
      color: 'text-sky-blue',
    },
    {
      label: t('customers'),
      value: 500,
      icon: <Users className='h-10 w-10' />,
      color: 'text-success',
    },
    {
      label: t('cities'),
      value: 20,
      icon: <MapPin className='h-10 w-10' />,
      color: 'text-red',
    },
  ]

  return (
    <section
      id='stats-section'
      className={cn(
        'grid w-full grid-cols-1 gap-8 text-center sm:grid-cols-3',
        className,
      )}
    >
      {stats.map(stat => (
        <StatCard key={stat.label} {...stat} />
      ))}
    </section>
  )
}

const StatCard: React.FC<Stat> = ({ label, value, icon, color }) => {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-50px' })
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (isInView) {
      let start = 0
      const duration = 1500 // ms
      const stepTime = 16
      const increment = value / (duration / stepTime)

      const timer = setInterval(() => {
        start += increment
        if (start >= value) {
          start = value
          clearInterval(timer)
        }
        setCount(Math.floor(start))
      }, stepTime)

      return () => clearInterval(timer)
    }
  }, [isInView, value])

  return (
    <Motion
      ref={ref}
      type='div'
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className='flex flex-col items-center space-y-3'
    >
      <div className={cn('rounded-full bg-white p-3 shadow-md', color)}>
        {icon}
      </div>
      <span className='text-navy text-4xl font-bold'>+{count}</span>
      <span className='text-gray-600'>{label}</span>
    </Motion>
  )
}
